import type { EquipmentPurpose } from '@/types/equipment'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { connectionKey, reservationsKey } from '@/lib/processStepKeys'
import { useProcessRunBatchReady } from '@/hooks/useProcessRunBatch'
import {
  processStepsApi,
  type CompleteExecutionPayload,
  type EndExecutionPayload,
  type EquipmentReservation,
  type ProcessFieldDefPayload,
  type ProcessStepConnection,
  type ProcessStepExecution,
  type ProcessStepSummary,
  type ReserveEquipmentItem,
  type ReserveInventoryPayload,
  type ReserveInventoryResult,
  type ReturnableLot,
  type ReturnInventoryPayload,
  type ReturnInventoryResult,
  type ScanEquipmentInput,
  type ScanEquipmentResult,
  type StartExecutionPayload,
  type UndoStartExecutionPayload,
  type UpdateSelectionsPayload,
  type UpsertProcessStepPayload,
} from '@/api/processStepsApi'
import { useTenant } from '@/providers/TenantProvider'

/** Query key for the tenant's process-step list: ['process-steps', tenantId, 'list']. */
const listKey = (tenantId: string | null) => ['process-steps', tenantId, 'list'] as const

/** Query key for a field's summary: ['process-steps', tenantId, fieldId, 'summary']. */
const summaryKey = (tenantId: string | null, fieldId: string | null) =>
  ['process-steps', tenantId, fieldId, 'summary'] as const

/** Forms that contain at least one Process field, with the fields each embeds. */
export function useFormsWithProcesses() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['process-steps', tenantId, 'forms'] as const,
    queryFn: () => processStepsApi.listFormsWithProcesses(tenantId as string),
    enabled: !!tenantId,
  })
}

/** Which process fields already have a connection (+ child-row counts) for the tenant. */
export function useProcessStepList() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: listKey(tenantId),
    queryFn: () => processStepsApi.list(tenantId as string),
    enabled: !!tenantId,
  })
}

/** The process-step connection for a field (null when it has none yet, or 404). */
export function useProcessStepConnection(fieldId: string | null, submissionId?: string | null) {
  const { selectedTenantId: tenantId } = useTenant()
  // Inside a run form, wait for its batch read, which fills this key (z8nrz7e8kw).
  const batchReady = useProcessRunBatchReady()
  return useQuery({
    queryKey: connectionKey(tenantId, fieldId, submissionId),
    queryFn: () =>
      processStepsApi.get(tenantId as string, fieldId as string, submissionId ?? undefined),
    enabled: !!tenantId && !!fieldId && batchReady,
  })
}

/**
 * The attribute DEFINITIONS a Field Attributes panel is built from.
 *
 * Per tenant, not per field — every step's attributes come from the same set —
 * so it is keyed without the field id and shared across steps.
 */
export function useProcessFieldDefs() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['process-steps', tenantId, 'field-defs'],
    queryFn: () => processStepsApi.listFieldDefs(tenantId as string),
    enabled: !!tenantId,
  })
}

/**
 * Defining the attributes themselves.
 *
 * Separate from `useProcessFieldDefs`, which only reads them: the editor that
 * writes is a dialog opened from the Field Attributes panel, and every write
 * answers with the whole list so the cache is replaced rather than merged.
 */
export function useManageProcessFieldDefs() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()

  const invalidate = () => {
    // The panel reads the defs AND every step's stored values through them, so a
    // definition change can alter what any step renders.
    void qc.invalidateQueries({ queryKey: ['process-steps', tenantId] })
  }

  const create = useMutation({
    mutationFn: (payload: ProcessFieldDefPayload) =>
      processStepsApi.createFieldDef(tenantId as string, payload),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: (input: { id: string; payload: Partial<Omit<ProcessFieldDefPayload, 'key'>> }) =>
      processStepsApi.updateFieldDef(tenantId as string, input.id, input.payload),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => processStepsApi.removeFieldDef(tenantId as string, id),
    onSuccess: invalidate,
  })

  return { create, update, remove }
}

/** The summary stats for a field's connection (null when it has none yet, or 404). */
export function useProcessStepSummary(fieldId: string | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: summaryKey(tenantId, fieldId),
    queryFn: () => processStepsApi.getSummary(tenantId as string, fieldId as string),
    enabled: !!tenantId && !!fieldId,
  })
}

/** Replace a field's process-step config, invalidating its connection + summary. */
export function useUpsertProcessStep() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()
  return useMutation<
    ProcessStepConnection,
    Error,
    { fieldId: string; payload: UpsertProcessStepPayload }
  >({
    mutationFn: ({ fieldId, payload }) =>
      processStepsApi.update(tenantId as string, fieldId, payload),
    onSuccess: (_data, { fieldId }) => {
      void qc.invalidateQueries({ queryKey: connectionKey(tenantId, fieldId) })
      void qc.invalidateQueries({ queryKey: summaryKey(tenantId, fieldId) })
      void qc.invalidateQueries({ queryKey: listKey(tenantId) })
    },
  })
}

/** Delete a field's process-step config, invalidating its connection + summary. */
export function useDeleteProcessStep() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()
  return useMutation<void, Error, { fieldId: string }>({
    mutationFn: ({ fieldId }) => processStepsApi.remove(tenantId as string, fieldId),
    // onSettled, not onSuccess: the delete flow treats a 404 as "already gone"
    // and carries on, but that path still rejects the mutation — so invalidating
    // only on success left the caches describing a connection the server no
    // longer has. Refetching after a genuine failure is merely a wasted read.
    onSettled: (_data, _error, { fieldId }) => {
      void qc.invalidateQueries({ queryKey: connectionKey(tenantId, fieldId) })
      void qc.invalidateQueries({ queryKey: summaryKey(tenantId, fieldId) })
      void qc.invalidateQueries({ queryKey: listKey(tenantId) })
    },
  })
}

// ─── Executions (P9) ─────────────────────────────────────────────────────────

/** Query key for a submission's executions: ['process-steps', tenantId, 'executions', submissionId]. */
const executionsKey = (tenantId: string | null, submissionId: string | null) =>
  ['process-steps', tenantId, 'executions', submissionId] as const

/**
 * Drop every step's executions for this tenant, whichever submission they are on.
 *
 * Assigning a PROCEDURE changes who owns steps across all of its forms — both
 * by cascading onto existing executions and by changing what the untouched ones
 * inherit. The caller doing that assign has no idea which submissions were
 * affected, and `staleTime` here is five minutes, so without this the operator
 * assigns someone and the step cards keep showing the old owner until they
 * reload. Broad on purpose: a procedure-level assign is rare, and refetching a
 * few step lists is cheaper than working out which ones moved.
 */
export function useInvalidateStepExecutions() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()
  return () =>
    void qc.invalidateQueries({ queryKey: ['process-steps', tenantId, 'executions'] })
}

/**
 * How often an open form re-reads its steps. A pick-list edit writes straight
 * into them (z8nrz7fjzc), often from the stockroom on another screen, and the
 * form has to show it without a reload.
 */
const EXECUTIONS_POLL_MS = 15_000

/** All process-step executions for a form submission (status per Process field). */
export function useProcessStepExecutions(submissionId: string | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: executionsKey(tenantId, submissionId),
    queryFn: () => processStepsApi.listExecutions(tenantId as string, submissionId as string),
    enabled: !!tenantId && !!submissionId,
    refetchInterval: (query) => (query.state.status === 'error' ? false : EXECUTIONS_POLL_MS),
    refetchOnWindowFocus: true,
  })
}

export type ProcessStepTransition =
  | { action: 'start'; submissionId: string; fieldId: string; payload: StartExecutionPayload }
  | { action: 'end'; submissionId: string; fieldId: string; payload: EndExecutionPayload }
  | { action: 'complete'; submissionId: string; fieldId: string; payload: CompleteExecutionPayload }

/** Apply a signed Start/End/Complete transition, refreshing the submission's executions. */
export function useProcessStepTransition() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()
  return useMutation<ProcessStepExecution, Error, ProcessStepTransition>({
    mutationFn: (t) => {
      const id = tenantId as string
      if (t.action === 'start') {
        return processStepsApi.startExecution(id, t.submissionId, t.fieldId, t.payload)
      }
      if (t.action === 'end') {
        return processStepsApi.endExecution(id, t.submissionId, t.fieldId, t.payload)
      }
      return processStepsApi.completeExecution(id, t.submissionId, t.fieldId, t.payload)
    },
    onSuccess: (_data, { submissionId, fieldId }) => {
      void qc.invalidateQueries({ queryKey: executionsKey(tenantId, submissionId) })
      // Start places new holds and End/Complete release or settle them, either
      // of which changes what is open to return — and the `[+]` control's own
      // query (`useReturnableInventory`) is keyed per requirement, so without
      // this its cached (often empty, pre-Start) result stands until something
      // else happens to touch it, which in practice meant a page reload.
      // Invalidated by (tenantId, submissionId, fieldId) prefix rather than
      // enumerating requirement ids, which this mutation does not have.
      void qc.invalidateQueries({
        queryKey: ['process-steps', tenantId, 'returnable-inventory', submissionId, fieldId],
      })
      // Start reserves, End/Complete consume or release — every one of these
      // changes a lot's held/available/on-hand quantity, which the Inventory
      // module's materials list and detail page read independently of this
      // module's own cache.
      void qc.invalidateQueries({ queryKey: ['inventory', 'materials'] })
      void qc.invalidateQueries({ queryKey: ['inventory', 'material'] })
    },
  })
}

/**
 * Void a start made in error (STARTED → ASSIGNED). Separate from
 * useProcessStepTransition because the payload differs — a required reason, no
 * selections — and the confirm panel wants its own pending/error state.
 */
export function useUndoStart() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()
  return useMutation<
    ProcessStepExecution,
    Error,
    { submissionId: string; fieldId: string; payload: UndoStartExecutionPayload }
  >({
    mutationFn: ({ submissionId, fieldId, payload }) =>
      processStepsApi.undoStartExecution(tenantId as string, submissionId, fieldId, payload),
    onSuccess: (_data, { submissionId, fieldId }) => {
      void qc.invalidateQueries({ queryKey: executionsKey(tenantId, submissionId) })
      // Undoing releases the step's equipment holds server-side. Without this
      // the modal keeps rendering holds that no longer exist.
      void qc.invalidateQueries({ queryKey: reservationsKey(tenantId, submissionId, fieldId) })
      // It releases inventory holds too — the Inventory module's own queries
      // read that quantity independently of this module's cache.
      void qc.invalidateQueries({ queryKey: ['inventory', 'materials'] })
      void qc.invalidateQueries({ queryKey: ['inventory', 'material'] })
      // …and the step's own open holds are gone with them. Three readers share
      // this key — `useStepOpenHolds` seeds allocations from it, the allocator
      // adds it back to what a lot can draw, and the `[+]` control offers it as
      // returnable — and its 15s staleTime meant reopening the step shortly
      // after an undo restored holds the server had already released. Cleared
      // by (tenantId, submissionId, fieldId) prefix, since this mutation does
      // not know the requirement ids.
      void qc.invalidateQueries({
        queryKey: ['process-steps', tenantId, 'returnable-inventory', submissionId, fieldId],
      })
    },
  })
}

// ─── Visibility (Slice C) ────────────────────────────────────────────────────

/**
 * Advisory availability for a window. Disabled until a window is known, since
 * "is this unit free" is meaningless without one. Short staleTime because a
 * reservation elsewhere invalidates it at any moment — and even fresh, it can
 * be stale by the time the operator clicks Reserve.
 */
export function useEquipmentAvailability(from: string | null, to: string | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['process-steps', tenantId, 'availability', from, to] as const,
    queryFn: () => processStepsApi.equipmentAvailability(tenantId as string, from!, to!),
    enabled: !!tenantId && !!from && !!to,
    staleTime: 30_000,
  })
}

/**
 * Availability per requirement window. Disabled until at least one window is
 * known — "is this unit free" is meaningless without a time. Short staleTime:
 * a booking elsewhere invalidates it at any moment, and even fresh it can be
 * stale by the time Reserve is pressed, which is why the 409 path stays.
 */
export function useAvailabilityForWindows(
  windows: {
    key: string
    from: string
    to: string
    runsPerDay?: number
    excludeReservationIds?: string[]
  }[],
) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['process-steps', tenantId, 'availability-windows', windows] as const,
    queryFn: () => processStepsApi.availabilityForWindows(tenantId as string, windows),
    enabled: !!tenantId && windows.length > 0,
    staleTime: 30_000,
  })
}

/**
 * The FEFO lot proposal for a step's materials.
 *
 * Enabled only once the operator has asked for it (`enabled`), because a
 * proposal is a suggestion about a run that has not been planned yet — fetching
 * it on mount would put lot numbers in front of someone who has not said when
 * they are starting. Short staleTime: another run reserving stock changes what
 * is proposable at any moment.
 */
export function useInventoryProposal(
  submissionId: string,
  fieldId: string,
  enabled: boolean,
) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['process-steps', tenantId, 'inventory-proposal', submissionId, fieldId] as const,
    queryFn: () => processStepsApi.inventoryProposal(tenantId as string, submissionId, fieldId),
    enabled: !!tenantId && !!submissionId && !!fieldId && enabled,
    staleTime: 30_000,
  })
}

/**
 * Query key for one requirement's open holds. Kept in a helper because the
 * mutation below has to invalidate exactly the entry the control is reading.
 */
const returnableKey = (
  tenantId: string | null,
  submissionId: string | null,
  fieldId: string | null,
  requirementId: string | null,
) =>
  ['process-steps', tenantId, 'returnable-inventory', submissionId, fieldId, requirementId] as const

/**
 * What is still open to return for one material requirement. Since
 * `reserveInventory` (z8nrz7dyfp follow-up) a tick in the Start allocator IS a
 * server-side hold already, not just a client-side draft — so this can return
 * something even before Start is signed, and the `[+]` control keeps it always
 * enabled rather than gating on mode.
 */
export function useReturnableInventory(
  submissionId: string | null,
  fieldId: string | null,
  requirementId: string | null,
  enabled: boolean,
) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: returnableKey(tenantId, submissionId, fieldId, requirementId),
    queryFn: () =>
      processStepsApi.getReturnableInventory(
        tenantId as string,
        submissionId as string,
        fieldId as string,
        requirementId as string,
      ),
    enabled: !!tenantId && !!submissionId && !!fieldId && !!requirementId && enabled,
    staleTime: 15_000,
  })
}

/**
 * What this step ALREADY HOLDS, per requirement and lot number — the server's
 * own answer, keyed the way the allocator is keyed.
 *
 * The Reserve button used to read a `useState` map of the calls this session
 * happened to make, so a reload forgot every hold that was already placed: the
 * step still held the stock, but the panel offered to reserve it again and the
 * row's badge called it merely "allocated". Nothing was lost server-side — the
 * endpoint reconciles and writes only the shortfall — but the operator was
 * being asked to redo work that was already done.
 *
 * One query per requirement, sharing `returnableKey` with the `[+]` control, so
 * TanStack serves both from a single fetch rather than doubling the requests.
 */
export function useStepOpenHolds(
  submissionId: string | null,
  fieldId: string | null,
  requirementIds: readonly string[],
): Record<string, Record<string, number>> {
  const { selectedTenantId: tenantId } = useTenant()
  const combine = useCallback(
    (queries: readonly { data?: ReturnableLot[] }[]) => {
      const byRequirement: Record<string, Record<string, number>> = {}
      requirementIds.forEach((requirementId, i) => {
        const byLot: Record<string, number> = {}
        for (const hold of queries[i]?.data ?? []) {
          byLot[hold.lotNumber] = (byLot[hold.lotNumber] ?? 0) + hold.qty
        }
        byRequirement[requirementId] = byLot
      })
      return byRequirement
    },
    [requirementIds],
  )
  const results = useQueries({
    queries: openHoldsQueries(tenantId, submissionId, fieldId, requirementIds),
    // Collapsed inside the query client, so the identity below changes only
    // when the DATA does. Returning the raw results array would hand callers a
    // fresh object every render and defeat every `useMemo` downstream of it —
    // which is also why `combine` is a stable callback rather than an inline
    // literal: TanStack memoises on it, and a new function each render would
    // rebuild the map every time regardless of whether anything had moved.
    combine,
  })
  return results
}

const openHoldsQueries = (
  tenantId: string | null,
  submissionId: string | null,
  fieldId: string | null,
  requirementIds: readonly string[],
) =>
  requirementIds.map((requirementId) => ({
    queryKey: returnableKey(tenantId, submissionId, fieldId, requirementId),
    queryFn: () =>
      processStepsApi.getReturnableInventory(
        tenantId as string,
        submissionId as string,
        fieldId as string,
        requirementId,
      ),
    enabled: !!tenantId && !!submissionId && !!fieldId,
    staleTime: 15_000,
  }))

const allFresh = (queries: readonly { isSuccess: boolean; isFetching: boolean }[]) =>
  queries.every((q) => q.isSuccess && !q.isFetching)

/**
 * Whether `useStepOpenHolds` is answering from the server NOW, not from cache.
 *
 * A pick list writes into a step's holds (z8nrz7fjzc); coming back to the form,
 * the cache still has the holds from before the pick while the refetch is in
 * flight. Anything that reads the holds once (the Start allocator's seed) must
 * wait for this, or it shows the old figures for the rest of the sitting.
 * Same queries and keys, so it adds no request.
 */
export function useStepHoldsFresh(
  submissionId: string | null,
  fieldId: string | null,
  requirementIds: readonly string[],
): boolean {
  const { selectedTenantId: tenantId } = useTenant()
  return useQueries({
    queries: openHoldsQueries(tenantId, submissionId, fieldId, requirementIds),
    combine: allFresh,
  })
}

/**
 * Place an unsigned hold on a lot the instant it's ticked in the Start
 * allocator (z8nrz7dyfp follow-up) — before the transition itself is signed.
 * Invalidates the same two queries `useReturnInventory` does, for the same
 * reason: this changes what the step holds before anything else does.
 */
export function useReserveInventory() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()
  return useMutation<
    ReserveInventoryResult,
    Error,
    {
      submissionId: string
      fieldId: string
      requirementId: string
      payload: ReserveInventoryPayload
    }
  >({
    mutationFn: ({ submissionId, fieldId, requirementId, payload }) =>
      processStepsApi.reserveInventory(
        tenantId as string,
        submissionId,
        fieldId,
        requirementId,
        payload,
      ),
    onSuccess: (_data, { submissionId, fieldId, requirementId }) => {
      void qc.invalidateQueries({
        queryKey: returnableKey(tenantId, submissionId, fieldId, requirementId),
      })
      void qc.invalidateQueries({ queryKey: executionsKey(tenantId, submissionId) })
      // This changes a lot's held/available quantity, which the Inventory
      // module's own materials list and detail page show independently of
      // anything process-steps caches — without this they read stale until a
      // manual reload, even though the reservation is already real.
      void qc.invalidateQueries({ queryKey: ['inventory', 'materials'] })
      void qc.invalidateQueries({ queryKey: ['inventory', 'material'] })
    },
  })
}

/**
 * Release a requirement's open holds early. Invalidates both the returnable
 * list (so the `[+]` disappears once nothing is left) and the executions entry
 * Start/End/Complete write to, since the return changes what the step holds.
 */
export function useReturnInventory() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()
  return useMutation<
    ReturnInventoryResult,
    Error,
    {
      submissionId: string
      fieldId: string
      requirementId: string
      payload: ReturnInventoryPayload
    }
  >({
    mutationFn: ({ submissionId, fieldId, requirementId, payload }) =>
      processStepsApi.returnInventory(
        tenantId as string,
        submissionId,
        fieldId,
        requirementId,
        payload,
      ),
    onSuccess: (_data, { submissionId, fieldId, requirementId }) => {
      void qc.invalidateQueries({
        queryKey: returnableKey(tenantId, submissionId, fieldId, requirementId),
      })
      void qc.invalidateQueries({ queryKey: executionsKey(tenantId, submissionId) })
      // This changes a lot's held/available quantity, which the Inventory
      // module's own materials list and detail page show independently of
      // anything process-steps caches — without this they read stale until a
      // manual reload, even though the reservation is already real.
      void qc.invalidateQueries({ queryKey: ['inventory', 'materials'] })
      void qc.invalidateQueries({ queryKey: ['inventory', 'material'] })
      // A lot returned to quarantine gets a release task (z8nrz7fjzc).
      void qc.invalidateQueries({ queryKey: ['maintenance-events'] })
    },
  })
}

/** Live holds in a window, tenant-wide — the equipment calendar's reservation layer. */
export function useReservationsInWindow(
  from: string | null,
  to: string | null,
  /** Only holds on equipment of these purposes (z8nrz7ewt2). Omitted: every purpose. */
  purposes?: readonly EquipmentPurpose[],
) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['process-steps', tenantId, 'reservations-window', from, to, purposes?.join(',') ?? 'all'] as const,
    queryFn: () => processStepsApi.reservationsInWindow(tenantId as string, from!, to!, purposes),
    enabled: !!tenantId && !!from && !!to,
  })
}

/** The signed-in user's assigned process steps. */
export function useAssignedProcessSteps(options: { enabled?: boolean } = {}) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['process-steps', tenantId, 'assigned-to-me'] as const,
    queryFn: () => processStepsApi.listAssignedToMe(tenantId as string),
    // `enabled: false` for someone who cannot run steps: the route would refuse.
    enabled: !!tenantId && (options.enabled ?? true),
  })
}

// ─── Assignment (Slice B) ────────────────────────────────────────────────────
//
// A step cannot start until it is assigned, and only the assignee may start it
// or record what it used. Claiming your own work needs `process.execute`;
// delegating to someone else needs `process.assign`.

/** Claim for yourself by omitting `assigneeId`; pass one to delegate. */
export function useAssignExecution() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()
  return useMutation<
    ProcessStepExecution,
    Error,
    { submissionId: string; fieldId: string; assigneeId?: string }
  >({
    mutationFn: ({ submissionId, fieldId, assigneeId }) =>
      processStepsApi.assignExecution(tenantId as string, submissionId, fieldId, assigneeId),
    onSuccess: (_data, { submissionId }) => {
      void qc.invalidateQueries({ queryKey: executionsKey(tenantId, submissionId) })
    },
  })
}

export function useUnassignExecution() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()
  return useMutation<ProcessStepExecution, Error, { submissionId: string; fieldId: string }>({
    mutationFn: ({ submissionId, fieldId }) =>
      processStepsApi.unassignExecution(tenantId as string, submissionId, fieldId),
    onSuccess: (_data, { submissionId }) => {
      void qc.invalidateQueries({ queryKey: executionsKey(tenantId, submissionId) })
    },
  })
}

/** Record equipment/lots while the step runs. Merges server-side; assignee only. */
export function useUpdateSelections() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()
  return useMutation<
    ProcessStepExecution,
    Error,
    { submissionId: string; fieldId: string; payload: UpdateSelectionsPayload }
  >({
    mutationFn: ({ submissionId, fieldId, payload }) =>
      processStepsApi.updateSelections(tenantId as string, submissionId, fieldId, payload),
    onSuccess: (_data, { submissionId }) => {
      void qc.invalidateQueries({ queryKey: executionsKey(tenantId, submissionId) })
    },
  })
}

/**
 * Persist a step's expected start before Start is signed.
 *
 * The card used to hold this in local state alone, so a reload showed an empty
 * expected start on a step whose machines were genuinely booked — the holds
 * outlived the plan they were computed from. Invalidates the submission's
 * executions so every card on the form reads the stored value back.
 */
export function useSetPlannedStart() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()
  return useMutation<
    ProcessStepExecution,
    Error,
    { submissionId: string; fieldId: string; plannedStart: string | null }
  >({
    mutationFn: ({ submissionId, fieldId, plannedStart }) =>
      processStepsApi.setPlannedStart(tenantId as string, submissionId, fieldId, plannedStart),
    onSuccess: (_data, { submissionId }) => {
      void qc.invalidateQueries({ queryKey: executionsKey(tenantId, submissionId) })
    },
  })
}

// ─── Reservations (P10 conflict-safe equipment) ─────────────────────────────

/** Query key for a step's live reservations: ['process-steps', tenantId, 'reservations', submissionId, fieldId]. */
/** Live equipment holds for a step's configured requirements. */
export function useProcessStepReservations(submissionId: string | null, fieldId: string | null) {
  const { selectedTenantId: tenantId } = useTenant()
  // Inside a run form, wait for its batch read, which fills this key (z8nrz7e8kw).
  const batchReady = useProcessRunBatchReady()
  return useQuery({
    queryKey: reservationsKey(tenantId, submissionId, fieldId),
    queryFn: () =>
      processStepsApi.listReservations(
        tenantId as string,
        submissionId as string,
        fieldId as string,
      ),
    enabled: !!tenantId && !!submissionId && !!fieldId && batchReady,
  })
}

/**
 * Booking or freeing a unit changes what every availability view should say, and
 * those live under sibling keys rather than the step's own. Invalidated by
 * prefix (not the whole `['process-steps', tenantId]` tree, which would also
 * refetch connections, summaries and executions that a hold cannot affect).
 */
function invalidateAvailability(
  qc: ReturnType<typeof useQueryClient>,
  tenantId: string | null,
) {
  // Sibling keys: 'availability-windows' does not prefix-match 'availability'.
  for (const scope of ['availability', 'availability-windows', 'reservations-window'] as const) {
    void qc.invalidateQueries({ queryKey: ['process-steps', tenantId, scope] })
  }
}

/**
 * Refresh everything a RUN-WIDE booking touches.
 *
 * The per-step hooks below invalidate one `(submissionId, fieldId)` because that
 * is all they book. Reserving from a procedure's schedule books across every
 * form in the run, and the caller doing it holds a plan, not a list of the
 * caches those rows live in — so this drops the whole `reservations` prefix
 * rather than trying to name each step.
 *
 * `executions` goes too: a step card seeds its expected start from the
 * procedure's schedule, so the cards are stale for the same reason the holds
 * are. `['equipment']` covers the equipment pages, whose unit rows and calendar
 * are the other place a hold is visible, and `['inventory']` the materials
 * lists, whose Reserved and Available columns a material hold moves.
 *
 * Broad on purpose — scheduling a run is rare, and `staleTime` here is five
 * minutes, which is how long an operator would otherwise stare at a step that
 * says nothing is reserved when it is.
 */
export function useInvalidateRunReservations() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()
  return () => {
    void qc.invalidateQueries({ queryKey: ['process-steps', tenantId, 'reservations'] })
    void qc.invalidateQueries({ queryKey: ['process-steps', tenantId, 'executions'] })
    invalidateAvailability(qc, tenantId)
    void qc.invalidateQueries({ queryKey: ['equipment'] })
    // Material is held too, and a hold changes Reserved/Available on every
    // materials list and lot row. Without this the inventory page keeps
    // reporting the stock as free until its five minutes are up.
    void qc.invalidateQueries({ queryKey: ['inventory'] })
  }
}

/** Reserve equipment for a step's requirements (all-or-nothing), refreshing its reservations. */
export function useReserveEquipment() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()
  return useMutation<
    EquipmentReservation[],
    Error,
    { submissionId: string; fieldId: string; items: ReserveEquipmentItem[] }
  >({
    mutationFn: ({ submissionId, fieldId, items }) =>
      processStepsApi.reserveEquipment(tenantId as string, submissionId, fieldId, items),
    onSuccess: (_data, { submissionId, fieldId }) => {
      void qc.invalidateQueries({ queryKey: reservationsKey(tenantId, submissionId, fieldId) })
      invalidateAvailability(qc, tenantId)
    },
  })
}

/**
 * Move every hold on a step to a new expected start.
 *
 * Invalidates what `useReserveEquipment` does: the step's holds now sit in
 * different windows, so every availability view that answered against the old
 * ones is stale.
 */
export function useRetimeReservations() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()
  return useMutation<
    EquipmentReservation[],
    Error,
    { submissionId: string; fieldId: string; startsAt: string; requirementId?: string }
  >({
    mutationFn: ({ submissionId, fieldId, startsAt, requirementId }) =>
      processStepsApi.retimeReservations(
        tenantId as string,
        submissionId,
        fieldId,
        startsAt,
        requirementId,
      ),
    onSuccess: (_data, { submissionId, fieldId }) => {
      void qc.invalidateQueries({ queryKey: reservationsKey(tenantId, submissionId, fieldId) })
      invalidateAvailability(qc, tenantId)
    },
  })
}

/**
 * Scan an equipment tag: verify the unit is the right one, and hold it.
 *
 * Invalidates exactly what `useReserveEquipment` does — a verified scan IS a
 * reservation, so the step's holds and every availability view are now stale.
 */
export function useScanEquipment() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()
  return useMutation<
    ScanEquipmentResult,
    Error,
    { submissionId: string; fieldId: string; input: ScanEquipmentInput }
  >({
    mutationFn: ({ submissionId, fieldId, input }) =>
      processStepsApi.scanEquipment(tenantId as string, submissionId, fieldId, input),
    onSuccess: (_data, { submissionId, fieldId }) => {
      void qc.invalidateQueries({ queryKey: reservationsKey(tenantId, submissionId, fieldId) })
      invalidateAvailability(qc, tenantId)
    },
  })
}

/** Release a single reservation, refreshing its owning step's reservations. */
/**
 * Mark a held unit as started by hand.
 *
 * Invalidates the step's holds so the row picks up its actual start. NOT the
 * availability views: the window is unchanged — the unit was already booked for
 * it — so nothing about what is free anywhere else has moved.
 */
export function useStartReservation() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()
  return useMutation<EquipmentReservation, Error, string>({
    mutationFn: (reservationId) =>
      processStepsApi.startReservation(tenantId as string, reservationId),
    onSuccess: (data) => {
      void qc.invalidateQueries({
        queryKey: reservationsKey(tenantId, data.submissionId, data.fieldId),
      })
    },
  })
}

export function useReleaseReservation() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()
  return useMutation<EquipmentReservation, Error, string>({
    mutationFn: (reservationId) =>
      processStepsApi.releaseReservation(tenantId as string, reservationId),
    onSuccess: (data) => {
      void qc.invalidateQueries({
        queryKey: reservationsKey(tenantId, data.submissionId, data.fieldId),
      })
      invalidateAvailability(qc, tenantId)
    },
  })
}

/** Convenience re-export of the summary type for consumers. */
export type { ProcessStepSummary }
